/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  ActualFileObject,
  FilePondFile,
  ProcessServerConfigFunction,
} from 'filepond';
import { html, unsafeCSS } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { property, state } from 'lit/decorators.js';
import { type UploadedFile } from './filepond-wrapper.wc';
// eslint-disable-next-line no-duplicate-imports
import './filepond-wrapper.wc';

import { EntityTypes } from './filepond.types';
import { BaseElement, customElement } from '../../base-element';
import { Translate } from '../../base-element/mixins/translation-mixin';
import styles from './filepond.scss?inline';

export interface FilepondProps {
  accountId?: string;
  entityId?: string;
  entityType?: EntityTypes;
  pictureSrc?: string;
  name?: string;
  readonly?: boolean;
  type?: 'image' | 'file';
  previewLabel?: string;
  previewCta?: string;
  accept?: string[];
  onChange?: ({
    url,
    pictureId,
    data,
    files,
  }: {
    url?: string;
    pictureId?: string;
    data?: unknown; // only for EntityTypes.ASSET
    files?: (ActualFileObject | File | undefined)[];
  }) => void;
  // eslint-disable-next-line no-use-before-define
  uploadFile?: SetProfilePicture;
}

type ConfigureProcessFunctionParams = Pick<
  FilepondProps,
  'accountId' | 'entityId' | 'entityType' | 'onChange' | 'uploadFile'
> & {
  files?: (ActualFileObject | File | undefined)[];
};

type ProcessServerConfigFunctionParams =
  Parameters<ProcessServerConfigFunction>;

interface ConfigureProcessFunction {
  (props: ConfigureProcessFunctionParams): ProcessServerConfigFunction;
}

export type SetProfilePictureParams = Omit<
  ConfigureProcessFunctionParams,
  'uploadFile'
> & {
  file?: ProcessServerConfigFunctionParams[1];
  files?: ProcessServerConfigFunctionParams[1][];
  load?: ProcessServerConfigFunctionParams[3];
  error?: ProcessServerConfigFunctionParams[4];
  progress?: ProcessServerConfigFunctionParams[5];
  abort?: ProcessServerConfigFunctionParams[6];
};

export interface SetProfilePicture {
  (props: SetProfilePictureParams): {
    abort: SetProfilePictureParams['abort'];
  };
}

export type UploadResponse = {
  accountId?: string;
  appletId?: string;
  id?: string;
  projectId?: string;
  publicId?: string;
  url?: string;
  userId?: string;
  createdBy?: string;
  createdAt?: Date;
};

const removeFile = ({
  accountId,
  entityId,
  entityType,
}: {
  accountId?: string;
  entityId?: string;
  entityType?: EntityTypes;
}) => {
  if (!accountId || !entityId || !entityType) return;

  const url = `${import.meta.env.FE_ADMIN_URL}/rest/upload-file-picture/${accountId}/${entityType}/${entityId}`;
  const request = new XMLHttpRequest();
  request.withCredentials = true;
  request.open('POST', url);
  request.send(undefined);
};

let callsAmount = 0;

const fakeUploadFunction: SetProfilePicture = ({
  file,
  files,
  load,
  error,
  progress,
  abort,
  onChange,
}) => {
  const returnObject = {
    abort: () => {
      // Let FilePond know the request has been cancelled
      abort?.();
    },
  };

  if (callsAmount) {
    callsAmount = 0;
    return returnObject;
  }

  if (!file) {
    error?.('No file provided');
    return returnObject;
  }

  // Simulate instant "upload complete"
  progress?.(true, file.size, file.size);
  load?.(file.name);

  onChange?.({
    files,
  });

  callsAmount += 1;

  return returnObject;
};

const uploadFileFunction: SetProfilePicture = ({
  accountId,
  entityId,
  entityType = EntityTypes.USER,
  file,
  load,
  error,
  progress,
  abort,
  onChange,
}) => {
  const url = entityId
    ? `${
        import.meta.env.FE_ADMIN_URL
      }/rest/upload-file-picture/${accountId}/${entityType}/${entityId}`
    : `${import.meta.env.FE_ADMIN_URL}/rest/upload-file/${accountId}`;

  const request = new XMLHttpRequest();
  request.withCredentials = true;
  request.open('POST', url);

  progress?.(true, 0, file?.size ?? 1);

  // Should call the progress method to update the progress to 100% before calling load
  // Setting computable to false switches the loading indicator to infinite mode
  request.upload.onprogress = (e) => {
    progress?.(e.lengthComputable, e.loaded, e.total);
  };

  // Should call the error method if something is wrong, should exit after
  request.onerror = () => {
    const errorMessage = `Network error occurred during file ${file ? 'upload' : 'deletion'}`;
    error?.(errorMessage);
  };

  // Should call the load method when done and pass the returned server file id
  // this server file id is then used later on when reverting or restoring a file
  // so your server knows which file to return without exposing that info to the client

  request.onload = () => {
    if (request.status >= 200 && request.status < 300) {
      // the load method accepts either a string (id) or an object
      load?.(request.responseText);

      const result: UploadResponse = JSON.parse(
        request.response
      ) as UploadResponse;
      const { url: imageUrl, id: pictureId } = result;
      onChange?.({
        url: imageUrl,
        pictureId,
      });
    } else {
      const errorMessage = file
        ? `Error uploading file (${request.status}): ${request.statusText || 'Server error occurred'}`
        : `Error deleting file (${request.status}): ${request.statusText || 'Server error occurred'}`;
      error?.(errorMessage);
    }
  };

  const formData = new FormData();

  if (file) {
    formData.append('file', file);
  }

  const body = file ? formData : undefined;

  request.send(body);

  // Should expose an abort method so the request can be cancelled
  return {
    abort: () => {
      // This function is entered if the user has tapped the cancel button
      request.abort();

      // Let FilePond know the request has been cancelled
      abort?.();
    },
  };
};

const configureProcessFunction: ConfigureProcessFunction =
  ({ files, accountId, entityId, entityType, onChange, uploadFile }) =>
  (...args) => {
    const [, file, , load, error, progress, abort] = args;

    return uploadFile?.({
      accountId,
      entityId,
      entityType,
      file,
      // @ts-ignore
      files,
      load,
      error,
      progress,
      abort,
      onChange,
    });
  };

@customElement('ps-filepond')
export class FilepondWC extends Translate(BaseElement) {
  static styles = unsafeCSS(styles);

  @property({ type: Array }) accept?: string[] = [];

  @property({ reflect: true, attribute: 'account-id' }) accountId?: string;

  @property({ reflect: true, attribute: 'entity-id' }) entityId?: string;

  @property({ reflect: true, attribute: 'entity-type' })
  entityType?: EntityTypes;

  @property({ attribute: 'picture-src' }) pictureSrc?: string;

  @property({ reflect: true }) label = '';

  @property() name?: string;

  @property({ reflect: true, attribute: 'preview-label' })
  previewLabel = 'Drag a photo here';

  @property({ reflect: true, attribute: 'preview-cta' }) previewCta =
    'Select a file';

  @property({ type: Object, reflect: true }) onUpload?: SetProfilePicture;

  @property({ reflect: true }) type?: 'image' | 'file' = 'image';

  @property({ reflect: true }) onChange: FilepondProps['onChange'];

  @property({ type: Boolean }) readonly?: boolean;

  @property({ type: Boolean }) immediatelyUpload?: boolean = true;

  @property({ type: Boolean, attribute: 'allow-multiple' })
  allowMultiple?: boolean;

  @property({ type: Boolean }) required?: boolean;

  @property({ type: Boolean, reflect: true }) vertical = false;

  @state() files: UploadedFile[] = [];

  removeObserver: MutationObserver;

  connectedCallback() {
    super.connectedCallback?.();
    this.addEventListener('click', this.onRemoveButtonClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.onRemoveButtonClick);
  }

  onRemoveButtonClick = (e: MouseEvent) => {
    const elementClicked = e.composedPath()[0] as HTMLElement;

    if (elementClicked.closest('.c-fp-upload__preview-remove')) {
      this.removeFileHandler();
    }
  };

  removeFileHandler = () => {
    this.pictureSrc = undefined;
    removeFile({
      entityId: this.entityId,
      entityType: this.entityType,
      accountId: this.accountId,
    });
    this.onChange?.({ url: undefined });
    this.emit('remove-file');
  };

  updateFileHandler = (e: FilePondFile[]) => {
    this.files = e.map(({ file }) => file);
  };

  render() {
    const labelFileProcessingComplete =
      // eslint-disable-next-line no-nested-ternary
      this.entityType === EntityTypes.USER
        ? 'Success, you look great!'
        : this.type === 'image'
          ? 'Success, image uploaded!'
          : 'Success, file uploaded!';

    if (this.readonly)
      return html`
        <div
          class=${classMap({
            'c-fp-upload': true,
            'c-fp-upload--vertical': this.vertical,
            'c-fp-upload--readonly': true,
            'c-fp-upload--readonly-has-image': !!this.pictureSrc,
            [`c-fp-upload--${this.entityType?.toLowerCase()}`]:
              !!this.entityType,
          })}
        >
          <div class=${classMap({
            'c-fp-upload__preview': true,
            'c-fp-upload__preview--vertical': this.vertical,
          })}>
            <div class="c-fp-upload__preview-icon">
              ${
                this.pictureSrc
                  ? html`<ps-avatar
                      size="2xlarge"
                      src=${this.pictureSrc}
                    ></ps-avatar>`
                  : html`<ps-icon
                      name="arrow-up"
                      weight="bold"
                      size="small"
                    ></ps-icon>`
              }
            </div>
            <div class="c-fp-upload__preview-content">
              <p class="c-fp-upload__preview-text">
                ${this.previewLabel}
              </p>
              <p class="c-fp-upload__preview-info">
                ${this.previewCta}
              </span></p>
            </div>
          </div>
        </div>
    `;

    return html`
      <div
        class=${`c-fp-upload c-fp-upload--${this.entityType?.toLowerCase()} ${this.vertical ? 'c-fp-upload--vertical' : ''}`}
      >
        ${this.label
          ? html` <p class="c-fp-upload__label-text">${this.label}</p> `
          : ''}
        <ps-filepond-wrapper
          .allowMultiple=${this.allowMultiple}
          .acceptedFileTypes=${this.accept}
          ?disabled=${this.readonly}
          .files=${this.files}
          type=${this.type}
          name=${ifDefined(this.name)}
          labelIdle=${`
              <div class="${`c-fp-upload__preview ${this.vertical ? 'c-fp-upload__preview--vertical' : ''}`}">
                <div class="c-fp-upload__preview-icon">
                  ${
                    this.pictureSrc
                      ? `<ps-avatar size="2xlarge" src=${this.pictureSrc}></ps-avatar>`
                      : `<ps-icon name="arrow-up" weight="bold" size="small"></ps-icon>`
                  }
                </div>
                <div class="c-fp-upload__preview-content">
                  <p class="c-fp-upload__preview-text">
                    ${this.pictureSrc ? labelFileProcessingComplete : this.previewLabel}
                  </p>
                  <p class="c-fp-upload__preview-info">
                    ${this.previewCta}
                  </span></p>
                </div>
                ${
                  this.pictureSrc
                    ? `
                      <button type="button" class="c-fp-upload__preview-remove">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                          <path d="m5 19 7-7m7-7-7 7m0 0 7 7L5 5" stroke="currentColor" stroke-width="1"></path>
                        </svg>
                      </button>
                    `
                    : ''
                }
              </div>
            `}
          labelFileProcessing="Uploading..."
          labelFileProcessingComplete=${labelFileProcessingComplete}
          labelFileProcessingError=${() => 'My server errored'}
          .onremovefile=${this.removeFileHandler}
          .onupdatefiles=${this.updateFileHandler}
          .server=${{
            revert: (uniqueFileId: string, load: () => void) => {
              this.files = [];
              // Should call the load method when done, no parameters required
              load();
            },
            process: configureProcessFunction({
              files: this.files,
              accountId: this.accountId,
              entityId: this.entityId,
              entityType: this.entityType,
              onChange: this.onChange,
              uploadFile:
                // @TODO create a functionality to upload multiple files on server and delete them on server, after delete this condition - || this.allowMultiple
                !this.immediatelyUpload || this.allowMultiple
                  ? fakeUploadFunction
                  : this.onUpload || uploadFileFunction,
            }),
          }}
        ></ps-filepond-wrapper>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-filepond': FilepondWC;
  }
  enum PSElementTagNameMap {
    'ps-filepond' = 'ps-filepond',
  }
}
